import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useChannelPartnerDetail,
  useChannelPartnerUpdate,
  CHANNEL_PARTNER_STATUS_LABELS,
  type ChannelPartnerDto,
  type ChannelPartnerStatus,
} from '@/hooks/useCrm';

// ─── Edit Dialog ──────────────────────────────────────────────────────────────

interface EditChannelPartnerDialogProps {
  partner: ChannelPartnerDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (updated: ChannelPartnerDto) => void;
}

function EditChannelPartnerDialog({
  partner,
  open,
  onOpenChange,
  onSaved,
}: EditChannelPartnerDialogProps) {
  const { loading, error, update } = useChannelPartnerUpdate();
  const [name, setName] = useState(partner.name);
  const [status, setStatus] = useState<ChannelPartnerStatus>(partner.status);
  const [commissionRate, setCommissionRate] = useState(partner.commissionRate ?? '');
  const [contactInfo, setContactInfo] = useState(partner.contactInfo ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await update(partner.id, {
      expectedVersion: partner.version,
      name: name.trim() || undefined,
      status,
      commissionRate: commissionRate.trim() || undefined,
      contactInfo: contactInfo.trim() || undefined,
    });
    if (result) {
      toast.success('渠道合作方信息已更新');
      onSaved(result);
      onOpenChange(false);
    } else if (error) {
      toast.error(error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">编辑渠道合作方</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="cp-edit-name">名称</Label>
              <Input id="cp-edit-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cp-edit-status">状态</Label>
              <select
                id="cp-edit-status"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value as ChannelPartnerStatus)}
              >
                {(Object.keys(CHANNEL_PARTNER_STATUS_LABELS) as ChannelPartnerStatus[]).map((s) => (
                  <option key={s} value={s}>{CHANNEL_PARTNER_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cp-edit-commission">佣金率</Label>
              <Input id="cp-edit-commission" value={commissionRate} onChange={(e) => setCommissionRate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cp-edit-contact">联系信息（JSON）</Label>
              <textarea
                id="cp-edit-contact"
                className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
                rows={3}
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '保存中…' : '保存'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChannelPartnerDetailPage() {
  // ── All hooks must be called unconditionally at the top level ──────────────
  const params = useParams<{ partnerId: string }>();
  const partnerId = params.partnerId;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();

  const { loading, fetch } = useChannelPartnerDetail();
  const [partner, setPartner] = useState<ChannelPartnerDto | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Derive permission flag after all hooks are called.
  const canRead = hasPermission(permissions, CRM_PERMISSIONS.CHANNEL_PARTNER_READ);

  useEffect(() => {
    // Gate API request: skip fetch when the user lacks read permission.
    if (!partnerId || !canRead) return;
    void (async () => {
      const result = await fetch(partnerId);
      if (result) setPartner(result);
    })();
  }, [canRead, fetch, partnerId]);

  // ── Permission early return — after all hooks ─────────────────────────────
  if (!canRead) {
    return <AccessDenied resource="渠道合作方详情" />;
  }

  if (loading && !partner) {
    return <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>;
  }

  if (!partner) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        渠道合作方不存在或无权访问。
        <button
          className="ml-2 text-primary hover:underline"
          onClick={() => navigate('/crm/channel-partners')}
        >
          返回列表
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            className="mb-1 text-sm text-muted-foreground hover:underline"
            onClick={() => navigate('/crm/channel-partners')}
          >
            ← 返回列表
          </button>
          <h2 className="text-xl font-semibold">{partner.name}</h2>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
          编辑
        </Button>
      </div>

      <Card className="p-4">
        <h3 className="mb-3 font-medium">基本信息</h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">状态</dt>
          <dd>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
              {CHANNEL_PARTNER_STATUS_LABELS[partner.status]}
            </span>
          </dd>
          <dt className="text-muted-foreground">佣金率</dt>
          <dd>{partner.commissionRate ?? '—'}</dd>
          <dt className="text-muted-foreground">联系信息</dt>
          <dd className="truncate">{partner.contactInfo ?? '—'}</dd>
          <dt className="text-muted-foreground">创建时间</dt>
          <dd>{new Date(partner.createdAt).toLocaleString('zh-CN')}</dd>
          <dt className="text-muted-foreground">更新时间</dt>
          <dd>{new Date(partner.updatedAt).toLocaleString('zh-CN')}</dd>
          <dt className="text-muted-foreground">版本</dt>
          <dd>{partner.version}</dd>
        </dl>
      </Card>

      {editDialogOpen && (
        <EditChannelPartnerDialog
          key={`cp-edit-${partner.id}-${partner.version}`}
          partner={partner}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSaved={(updated) => setPartner(updated)}
        />
      )}
    </div>
  );
}
